import React, { useMemo, useState, useCallback } from 'react';
import { Select, Modal, message } from 'antd';
import { updateAssistant } from '../../services/api';

/*
* Admin-only owner transfer for assistants 
*/

const AssistantOwnerSelect = ({
  ownerId,
  options,
  loading = false,
  assistantId,
  assistantName,
  keycloak,
  onUpdated,
  className,
  style,
  minWidth = 0,
  getPopupContainer,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState({ value: '', label: '' });

  const mergedOptions = useMemo(() => {
    const oid = String(ownerId ?? '').trim();
    if (!oid || options.some((o) => String(o.value) === oid)) return options;
    return [{ value: oid, label: `User ${oid.slice(0, 8)}…` }, ...options];
  }, [ownerId, options]);

  const normalizedOwner = String(ownerId ?? '').trim();

  const handleChange = (newVal) => {
    const next = String(newVal ?? '').trim();
    if (next === normalizedOwner) return;

    const nextLabel = next
      ? mergedOptions.find((o) => String(o.value) === next)?.label || 'Selected user'
      : 'Platform (no individual owner)';

    setPending({ value: next, label: nextLabel });
    setConfirmOpen(true);
  };

  const submitTransfer = useCallback(async () => {
    try {
      await updateAssistant(keycloak, assistantId, { owner: pending.value });
      message.success('Owner updated');
      onUpdated?.();
      setConfirmOpen(false);
    } catch (e) {
      message.error(e?.message || 'Failed to update owner');
      throw e;
    }
  }, [pending.value, keycloak, assistantId, onUpdated]);

  return (
    <>
      <Select
        className={className}
        style={{ minWidth: minWidth || undefined, ...style }}
        size="small"
        variant="borderless"
        showSearch
        loading={loading}
        placeholder="Owner"
        value={normalizedOwner || undefined}
        options={mergedOptions}
        optionFilterProp="label"
        onChange={handleChange}
        getPopupContainer={getPopupContainer}
        popupMatchSelectWidth={false}
      />
      <Modal
        title="Transfer ownership?"
        open={confirmOpen}
        okText="Confirm"
        okType="primary"
        cancelText="Cancel"
        centered
        zIndex={1100}
        destroyOnClose
        onOk={submitTransfer}
        onCancel={() => setConfirmOpen(false)}
      >
        {assistantName ? (
          <p style={{ margin: 0 }}>
            The assistant &ldquo;{assistantName}&rdquo; will be assigned to <strong>{pending.label}</strong>.
          </p>
        ) : (
          <p style={{ margin: 0 }}>
            Assign to <strong>{pending.label}</strong>.
          </p>
        )}
      </Modal>
    </>
  );
};

export default AssistantOwnerSelect;
